import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";

import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { makePostCallForMyPaOrders } from "../apis/makePostCallForMyPaOrders";
import { MyMedicalPaOrdersState } from "./useMyPaOrdersTableData";

/**
 * Triggers a Postgres search whenever the filter payload or auth status options change,
 * provided the initial data fetch has already completed. Resets the table context's
 * lastBatchFetched to 0 on each new filter/search execution.
 *
 * @param filterPayload - Current search parameters (null/undefined skips the fetch)
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param setTableData - State setter for the transformed table row data
 * @param setMyMedicalPaOrdersState - State setter for the orders loading/data/error state
 * @param setPaginationResetCount - Incremented to signal a pagination reset
 */
export const useFilterAndSearchEffect = (
  filterPayload: SearchForMedicalPaOrdersFromPostgresParams | null | undefined,
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
  setMyMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersState>
  >,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext } = useTablesContext();
  const isFirstPayload = useRef(true);

  useEffect(() => {
    if (
      filterPayload &&
      initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      if (isFirstPayload.current) {
        isFirstPayload.current = false;
        return;
      }

      makePostCallForMyPaOrders(
        filterPayload,
        authStatusOptions,
        setMyMedicalPaOrdersState,
        setTableData,
        setPaginationResetCount,
      );
      setTableDataForContext(TableNames.MY_PA_ORDERS_FROM_ALGOLIA, {
        lastBatchFetched: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPayload, authStatusOptions]);
};
